import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, User, Store, Save, MapPin, Trophy } from 'lucide-react';

const Profile = () => {
    const { user, updateUser, logout } = useAuth();
    const navigate = useNavigate();
    
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        display_name: '',
        is_lgs: false,
        lgs_name: '',
        lgs_address: '',
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                display_name: user.display_name || user.name || '',
                is_lgs: user.is_lgs || false,
                lgs_name: user.lgs_name || '',
                lgs_address: user.lgs_address || '',
            });
        }
    }, [user]);

    const handleSave = async () => {
        try {
            setSaving(true);
            const response = await usersAPI.updateMe(formData);
            updateUser(response.data);
            toast.success('Profile updated successfully!');
            setEditing(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="matrix-loader" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="profile-page">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-[#00ff41]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-[#00ff41] tracking-wider">
                            USER_PROFILE
                        </h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-10 px-4">
                <div className="container mx-auto max-w-2xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="terminal-window"
                    >
                        <div className="terminal-header">
                            <div className="terminal-dot red" />
                            <div className="terminal-dot yellow" />
                            <div className="terminal-dot green" />
                            <span className="ml-4 text-[#00ff41] text-sm">user_data.sys</span>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Avatar */}
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 border-2 border-[#00ff41] overflow-hidden">
                                    {user.picture ? (
                                        <img 
                                            src={user.picture} 
                                            alt={user.name}
                                            className="w-full h-full object-cover filter grayscale sepia hue-rotate-90 saturate-200"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-[#003b00] flex items-center justify-center">
                                            <User className="w-10 h-10 text-[#00ff41]" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-[#00ff41]">
                                        {user.display_name || user.name}
                                    </h2>
                                    <p className="text-[#008f11] text-sm">{user.email}</p>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-[#0a0a0a] border border-[#003b00] p-4 text-center">
                                    <Trophy className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                    <p className="text-3xl font-bold text-[#00ff41]">{user.games_hosted || 0}</p>
                                    <p className="text-xs text-[#008f11] uppercase tracking-wider">Games Hosted</p>
                                </div>
                                <div className="bg-[#0a0a0a] border border-[#003b00] p-4 text-center">
                                    <User className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                    <p className="text-3xl font-bold text-[#00ff41]">{user.games_joined || 0}</p>
                                    <p className="text-xs text-[#008f11] uppercase tracking-wider">Games Joined</p>
                                </div>
                            </div>

                            {/* Edit Form */}
                            {editing ? (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm text-[#008f11] mb-1 block">Display Name</label>
                                        <Input
                                            data-testid="display-name-input"
                                            value={formData.display_name}
                                            onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                                            className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-[#003b00]">
                                        <div className="flex items-center gap-2">
                                            <Store className="w-5 h-5 text-[#00ff41]" />
                                            <span className="text-[#00ff41]">Local Game Store Account</span>
                                        </div>
                                        <Switch
                                            checked={formData.is_lgs}
                                            onCheckedChange={(checked) => setFormData({...formData, is_lgs: checked})}
                                        />
                                    </div>

                                    {formData.is_lgs && (
                                        <div className="space-y-4 p-4 border border-[#003b00]">
                                            <div>
                                                <label className="text-sm text-[#008f11] mb-1 block">Store Name</label>
                                                <Input
                                                    data-testid="lgs-name-input"
                                                    value={formData.lgs_name}
                                                    onChange={(e) => setFormData({...formData, lgs_name: e.target.value})}
                                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm text-[#008f11] mb-1 block">Store Address</label>
                                                <Input
                                                    data-testid="lgs-address-input"
                                                    value={formData.lgs_address}
                                                    onChange={(e) => setFormData({...formData, lgs_address: e.target.value})}
                                                    className="bg-[#050505] border-[#003b00] text-[#00ff41]"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <Button
                                            data-testid="save-profile-btn"
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="flex-1 bg-[#00ff41] text-black"
                                        >
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? 'SAVING...' : 'SAVE_CHANGES'}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setEditing(false)}
                                            className="border-[#003b00] text-[#00ff41]"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="data-row">
                                        <p className="data-label">Display Name</p>
                                        <p className="data-value">{user.display_name || user.name}</p>
                                    </div>

                                    {user.is_lgs && (
                                        <>
                                            <div className="data-row">
                                                <p className="data-label">Store Name</p>
                                                <p className="data-value">{user.lgs_name}</p>
                                            </div>
                                            <div className="data-row">
                                                <p className="data-label">Store Address</p>
                                                <p className="data-value">{user.lgs_address}</p>
                                            </div>
                                        </>
                                    )}

                                    <Button
                                        data-testid="edit-profile-btn"
                                        onClick={() => setEditing(true)}
                                        className="w-full bg-[#00ff41] text-black"
                                    >
                                        EDIT_PROFILE
                                    </Button>
                                </div>
                            )}

                            {/* Logout */}
                            <div className="pt-4 border-t border-[#003b00]">
                                <Button
                                    data-testid="profile-logout-btn"
                                    variant="destructive"
                                    onClick={() => {
                                        logout();
                                        navigate('/');
                                    }}
                                    className="w-full"
                                >
                                    DISCONNECT
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
};

export default Profile;
